import { useEffect } from "react";
import { useAppSelector } from "app/store";
import { useTeamManagement } from "./useTeamManagement";
import { Team } from "models/schemas";
import { useTeamName } from "pages/team_settings/hooks";

/**
 * Hook to handle team switching when an object from a different team is loaded
 * @param teamName The data source team name
 */
export const useCheckTeamSwitch = (teamName: string) => {
  const { currentResourceTeam, headerSelectedTeam } = useTeamName();
  const allTeams: any = useAppSelector((state) => state.teamSettings?.allTeams);

  const {
    updateSelectedTeam,
    updateHeaderSelectedTeam,
    updateUserCriticalnessInTeam,
    updateTeamDataAndSchemas,
  } = useTeamManagement();

  useEffect(() => {
    if (!teamName || !allTeams?.teams) {
      return;
    }

    // If teamName is a sub-team, resolve to its parent for header display only
    // When users navigate to a sub-team audit, teamName is the sub-team
    // (e.g., itx_compliance_apac). This resolves it to the parent for the header
    const currentTeam = allTeams.teams.find(
      (team: Team) => team.teamId === teamName
    );
    const parentTeamName = currentTeam?.parentTeamResourceId || teamName;
    const parentTeam = currentTeam?.parentTeamResourceId
      ? allTeams.teams.find((team: Team) => team.teamId === parentTeamName)
      : currentTeam;

    const isTeamMember = allTeams.teams.some(
      (team: Team) => team.teamId === teamName
    );

    if (isTeamMember) {
      // Header always shows the parent team (dropdown selection)
      const headerTeamName = parentTeam ? parentTeamName : teamName;
      const teamDescription = parentTeam?.teamDescription || "";

      if (headerTeamName !== headerSelectedTeam) {
        updateHeaderSelectedTeam(headerTeamName, teamDescription);
      }

      if (teamName !== currentResourceTeam) {
        updateSelectedTeam(teamName);

        // Only update team-specific data when data team changes
        const userCriticalMap: { [key: string]: boolean } = {};
        const teamSettingsMap = new Map<string, Team>();

        allTeams.teams.forEach((team: Team) => {
          userCriticalMap[team.teamId] = team.isCritical;
          teamSettingsMap.set(team.teamId, team);
        });

        updateUserCriticalnessInTeam(teamName, userCriticalMap);
        updateTeamDataAndSchemas(teamName, teamSettingsMap);
      }
    } else {
      // Guest logic
      if (headerSelectedTeam !== "guest") {
        updateHeaderSelectedTeam("guest", "Guest");
      }
      if (currentResourceTeam !== teamName) {
        updateSelectedTeam(teamName);
      }
    }
  }, [
    teamName,
    headerSelectedTeam,
    currentResourceTeam,
    allTeams,
    updateSelectedTeam,
    updateHeaderSelectedTeam,
    updateUserCriticalnessInTeam,
    updateTeamDataAndSchemas,
  ]);
};
